import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {AbstractContactModel} from '@app/models/abstract-contact.model';
import {ContactModel} from '@app/models/contact.model';
import {GtProducts} from '@app/models/enums/gt-products.enum';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtCallControlService} from '@app/services/gt-call-control.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {JsSIPService} from '@app/services/gt-jssip.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {Click2callComponent} from '@app/shared/click2call/click2call.component';
import {environment} from '@environment';
import {Department} from '@gt-api';
import {TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CallLogStringUtil} from '@app/shared/utils/call-log-string-util';

@Component({
  selector: 'gt-contact-detail',
  templateUrl: './contact-detail.component.html',
  styleUrls: ['./contact-detail.component.scss']
})
export class ContactDetailComponent implements OnInit, OnDestroy {
  @Input() contact: AbstractContactModel;
  @Output() back = new EventEmitter<void>();
  @Input() showCancelButton = true;
  title: string;
  editMode = false;

  showDisplayNumberSelector: boolean;
  displayNumber: string;
  registered = false;
  amICallee = false;
  ongoingCall = false;
  departments: Department[];
  showDepartmentSelector = false;
  hasDepartments = environment.hasDepartments;
  products = GtProducts;
  address: string;
  private clonedAddress: string;

  private destroyed = new Subject<void>();
  isAdmin: boolean;
  private clonedContact: AbstractContactModel;

  constructor(
    private jsSIPService: JsSIPService,
    private router: Router,
    private callControlService: GtCallControlService,
    private dialog: MatDialog,
    private authenticationService: GtAuthenticationService,
    private translateService: TranslateService,
    private directoryService: GtDirectoryService,
    private notificationService: GtNotificationService
  ) {
    this.jsSIPService.registered.pipe(takeUntil(this.destroyed)).subscribe(b => (this.registered = b));

    this.jsSIPService.onSessionProgress.pipe(takeUntil(this.destroyed)).subscribe(() => (this.ongoingCall = true));
    this.jsSIPService.onIncomingCall.pipe(takeUntil(this.destroyed)).subscribe(caller => {
      if (caller) {
        this.amICallee = true;
        this.ongoingCall = true;
      }
    });
    this.jsSIPService.onSessionEnded.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.amICallee = false;
      this.ongoingCall = false;
    });
  }

  ngOnInit() {
    this.showDisplayNumberSelector = false;

    this.authenticationService.isAdmin().subscribe(isAdmin => {
      this.isAdmin = isAdmin;
    });
    this.directoryService.getOrganizationDepartments(this.authenticationService.oname).subscribe(
      r => (this.departments = r),
      _ => this.notificationService.errorNotification.next(this.translateService.instant('sms.error_sending'))
    );
    if (this.contact) {
      if (!this.contact.isStored()) {
        this.enableEditMode();
      }
      this.setTitle();
      this.showDepartmentSelector = environment.hasDepartments && this.contact.isEndUser();
      if (!this.contact.isServiceNumber()) {
        const contactModel = this.contact as ContactModel;
        this.address = contactModel.address;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.editMode) {
      this.resetContact();
    }
    this.destroyed.next();
    this.destroyed.complete();
  }

  goBack() {
    this.back.emit();
  }

  setTitle() {
    if (this.contact) {
      if (this.contact.name) {
        if (this.contact.isStored()) {
          this.title = this.contact.name;
        } else {
          this.title = this.contact.name + ` (${this.translateService.instant('contact_detail.not_stored')})`;
        }
      } else {
        this.title = this.translateService.instant('contact_detail.new');
      }
    }
  }

  callContactVOIP(callee: string) {
    this.showDisplayNumberSelector = false;
    this.router.navigate(['phone']);
    this.jsSIPService.call(callee);
  }

  transferCallToContactVOIP(transferee: string) {
    this.router.navigate(['phone']);
    this.jsSIPService.transfer(transferee);
  }

  private callContactWithDisplayNumber(callee: string, displayNumber: string) {
    this.callControlService
      .clickToCallWithDisplayNumber(
        this.authenticationService.oname,
        this.authenticationService.login,
        this.authenticationService.userData.fullNumber,
        callee,
        displayNumber
      )
      .subscribe(response => {
        this.goBack();
        if (response.code === 200) {
          this.notificationService.normalNotification.next(this.translateService.instant('contact_detail.call_init'));
        } else {
          this.notificationService.errorNotification.next(this.translateService.instant('contact_detail.call_init_failed'));
        }
      });
  }

  canEdit(): boolean {
    return this.isAdmin || this.authenticationService.login === this.contact.login || this.contact.isContact();
  }

  enableEditMode() {
    if (this.canEdit()) {
      this.editMode = true;
      this.clonedContact = {...this.contact} as AbstractContactModel;
      this.clonedAddress = this.address;
    }
  }

  updateContact() {
    if (this.contact instanceof ContactModel) {
      const contact = this.contact as ContactModel;
      contact.address = this.address;
      this.directoryService
        .editContact(this.authenticationService.oname, this.authenticationService.login, contact.getBackendModel())
        .subscribe(
          () => {
            this.notificationService.normalNotification.next(this.translateService.instant('contact_detail.updated'));
            this.editMode = false;
            this.setTitle();
          },
          error => {
            this.notificationService.errorNotification.next(this.translateService.instant('contact_detail.updated_error'));
          }
        );
    }
  }

  cancel() {
    if (this.contact.isStored()) {
      this.editMode = false;
      this.resetContact();
    } else {
      this.goBack();
    }
  }

  save() {
    if (this.contact.isStored()) {
      this.updateContact();
    } else {
      this.createContact();
    }
  }

  createContact() {
    if (this.contact instanceof ContactModel) {
      const contact = this.contact as ContactModel;
      contact.address = this.address;
      if (!contact.isStored()) {
        this.directoryService
          .createContact(this.authenticationService.oname, this.authenticationService.login, contact.getBackendModel())
          .subscribe(
            newContact => {
              this.contact.id = newContact.id;
              this.notificationService.normalNotification.next(this.translateService.instant('contact_detail.created'));
              this.editMode = false;
              this.setTitle();
            },
            error => {
              this.notificationService.errorNotification.next(this.translateService.instant('contact_detail.created_error'));
            }
          );
      }
    }
  }

  deleteContact() {
    if (this.contact instanceof ContactModel) {
      const contact = this.contact as ContactModel;
      if (contact.isStored() && (contact.isContact() || contact.isInternalContact())) {
        this.directoryService
          .deleteContact(this.authenticationService.oname, this.authenticationService.login, contact.getBackendModel())
          .subscribe(
            () => {
              this.notificationService.normalNotification.next(this.translateService.instant('contact_detail.deleted'));
              this.goBack();
            },
            error => {
              this.notificationService.errorNotification.next(this.translateService.instant('contact_detail.deleted_error'));
            }
          );
      }
    }
  }

  showClick2Call() {
    const dialogRef = this.dialog.open(Click2callComponent, {minWidth: 500});
    dialogRef.componentInstance.canEdit = false;
    dialogRef.componentInstance.initialPhoneNumber = this.contact.phoneNumber;
    dialogRef.componentInstance.name = this.contact.name;
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.callContactWithDisplayNumber(result.phoneNumber, result.displayNumber);
      }
      this.showDisplayNumberSelector = false;
    });
  }

  navigateToSendMsg() {
    this.router.navigate(['/sms'], { queryParams: { receivers: this.contact.phoneNumber } });
    this.dialog.closeAll();
  }

  setDepartment(name: string) {
    if (this.contact instanceof ContactModel) {
      this.contact.department = name;
      this.contact.departmentId = this.departments.find(e => e.name === name).id;
    }
  }

  private resetContact() {
    this.contact.name = this.clonedContact.name;
    this.contact.phoneNumber = this.clonedContact.phoneNumber;
    this.contact.shortNumber = this.clonedContact.shortNumber;
    this.contact.email = this.clonedContact.email;
    this.contact.department = this.clonedContact.department;
    if (this.contact instanceof ContactModel) {
      this.contact.departmentId = (this.clonedContact as ContactModel).departmentId;
    }
    this.contact.status = this.clonedContact.status;
  }

  displayNumberChanged(displayNumber: string) {
    this.displayNumber = displayNumber;
  }

  numberChanged($event) {
    this.contact.phoneNumber = $event;
  }

  getContactName() {
    return CallLogStringUtil.getPrettifyName(this.contact.firstName, this.contact.lastName);
  }
}
